import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { environment } from "../../../../environments/environment";
import { CompProduct } from "../../models/comp-product";
import { CompProductFacade } from "../../services/comp-product-facade";
import { GlobalStateService } from "../../../services/global-state.service";
import {
  CheckBoxDirective,
  NumericTextBoxComponent,
  ErrorComponent,
} from "@progress/kendo-angular-inputs";
import { ButtonComponent } from "@progress/kendo-angular-buttons";
import { AsyncPipe } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ProductFacade } from "../../services/product-facade";
import { QuoteCompProduct, QuoteLineDtl } from "../../models/quote-line-dtl";
import { DecimalMath } from "../../utils/utils";

export const lengthMultipleValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const selected = control.get("selected");
  const qty = control.get("qty");
  const packaging = control.get("packaging");
  const isLengthValid = selected.value && qty.value % packaging.value === 0;
  qty.setErrors(!selected.value || isLengthValid ? null : { byLength: true });
  return !selected.value || isLengthValid ? null : { byLength: true };
};

@Component({
  selector: "app-comp-product-container",
  templateUrl: "./comp-product-container.component.html",
  styleUrls: ["./comp-product-container.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CheckBoxDirective,
    NumericTextBoxComponent,
    ErrorComponent,
    ButtonComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class CompProductContainerComponent implements OnInit {
  private fb = inject(FormBuilder);
  compStepFacade = inject(CompProductFacade);
  private productFacade = inject(ProductFacade);
  globalStateSvc = inject(GlobalStateService);

  readonly imagePath: string = `${environment.webApiUrl}/Images/Product`;

  quoteLineDtl: QuoteLineDtl;

  selectedValues: string[] = [];
  focusedValue: string;
  currentStepDesc: string;
  currentStepCompProducts: CompProduct[];
  isSkippable: boolean | null;

  form: FormGroup = new FormGroup({});

  constructor() {
    this.quoteLineDtl = this.productFacade.getSnapShot().quoteLineDtl;
  }

  public getImgSource(item: CompProduct): string {
    return item.imageUrl
      ? `${this.imagePath}/${item.childPartNum}/${item.imageUrl}`
      : "assets/Sans-image.png";
  }

  btnDisabled(): boolean {               
    return (
      !this.isSkippable &&
      !!this.currentStepCompProducts[0] &&
      (this.form.invalid ||
        (this.currentStepCompProducts[0].mandatory &&
          this.selectedValues.length === 0))
    );
  }

  ngOnInit(): void {
    // Create Current Step Form
    this.compStepFacade.currentStep$.subscribe((r) => {
      if (r) {
        this.currentStepDesc = r?.stepName;
        this.currentStepCompProducts = r.compProducts ?? [];
        this.selectedValues = r.selectedCompParts ?? [];
        this.createCompPart(r.compProducts);
        this.isSkippable = r.isSkippable;
      }
    });
  }

  nextStepClick() {
    this.compStepFacade.goToNextStep();
    this.selectedValues = [];
  }

  goToSummary() {
    this.compStepFacade.goToSummary();
  }

  updateSelectedCompProduct(partNum: string, mandatory: boolean) {
    if (this.currentStepCompProducts[0].mandatory) {
      // Reset selected part
      this.selectedValues = [];
      this.currentStepCompProducts.forEach((r) => (<FormGroup>this.form.controls[r.childPartNum]).controls["selected"].setValue(false));

      (<FormGroup>this.form.controls[partNum]).controls["selected"].setValue(true);
      this.selectedValues = this.selectedValues.concat([partNum]);
      this.compStepFacade.updateCurrentStepSelectedCompPart(this.selectedValues);
      return;
    }

    if (this.selectedValues.includes(partNum)) {
      // Remove
      this.selectedValues = this.selectedValues.filter((x: string) => partNum !== x);
    } else {
      // Add
      this.selectedValues = this.selectedValues.concat([partNum]);
    }

    this.compStepFacade.updateCurrentStepSelectedCompPart(this.selectedValues);
  }

  onBlur() {
    this.focusedValue = "";
  }

  onFocus(partNum: string) {
    if (!this.selectedValues.includes(partNum)) {
      this.selectedValues = this.selectedValues.concat([partNum]);
      this.compStepFacade.updateCurrentStepSelectedCompPart(
        this.selectedValues,
      );
      this.focusedValue = partNum;
    }
  }

  private createCompPart(compProducts: CompProduct[]) {
    for (const compProduct of compProducts) {
      if (this.form.controls[compProduct.childPartNum]) {
        this.form.removeControl(compProduct.childPartNum, { emitEvent: false });
      }
      const compPartForm = this.fb.group(
        {
          selected: [this.selectedValues.includes(compProduct.childPartNum)],
          partNum: [compProduct.childPartNum],
          suggQty: [compProduct.suggQty],
          qty: [compProduct.qty],
          packaging: [compProduct.childPackaging],
        },
        { validators: lengthMultipleValidator },
      );

      compPartForm.valueChanges.subscribe((val) => {
        this.compStepFacade.updateCompProdQty(val.partNum, val.qty);
      });
        this.form.addControl(compProduct.childPartNum, compPartForm);

      if (this.globalStateSvc.isQuoteLineEditing) {
        
        const compPart = this.quoteLineDtl.compProduct.filter((r) => r.lastLevel === compProduct.childLastLevel);

        if(this.productFacade.isQuoteLineEditing &&
           this.quoteLineDtl.quoteLineApplied &&
            compPart.length > 0) {

            if (compPart.length > 1) {
                const foundCompPart:QuoteCompProduct = this.quoteLineDtl.compProduct.find((r) => r.lastLevel === compProduct.childLastLevel && r.partNum === compProduct.childPartNum);
                if (foundCompPart) {
                    compPartForm.controls.selected.setValue(true);
                    var qty = this.convertToChildUOM(foundCompPart,compProduct);
                    compPartForm.controls.qty.setValue(qty);
                    this.updateSelectedCompProduct(compProduct.childPartNum, compProduct.mandatory);
                }
            }
            else if (compPart.length === 1) {
                compPartForm.controls.selected.setValue(true);
                var qty = this.convertToChildUOM(compPart[0], compProduct);
                compPartForm.controls.qty.setValue(qty);
                this.updateSelectedCompProduct(compProduct.childPartNum, compProduct.mandatory);
            }                  
        }
      }
    }
  }


  private convertToChildUOM(quoteCompProd: QuoteCompProduct, compProduct: CompProduct): number {
    const priceListUOM = compProduct.partUOMs.find(
      (r) => r.uomCode === quoteCompProd.uom,
    );
    const originalQty = quoteCompProd.quantity;
    const factor = priceListUOM.convFactor;
    const newQty = Math.ceil(originalQty * factor);

    switch (priceListUOM.rounding) {
      case "RND":
        return DecimalMath.round(newQty, priceListUOM.numOfDec);
        break;
      case "UP":
        return DecimalMath.round(newQty, priceListUOM.numOfDec);
        break;
      case "DWN":
        return DecimalMath.round(newQty, priceListUOM.numOfDec);
        break;
      default:
        return DecimalMath.trunc(newQty, priceListUOM.numOfDec);
    }
  }
}
